import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  Form,
  FormButton,
  showNotification,
} from 'platform/components';
import {Align, Grid, Text, VStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {Trans} from 'react-i18next';

import {isNotNil} from 'ramda';

import {useGetMessagingAllowlistQuery, useUpdateMessagingAllowlistMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {handleApiError, Main} from '@dms/shared';

import {PatternList} from './components/PatternList';
import {CHANNELS} from './constants/CHANNELS';
import {
  MessagingAllowlistFormSchema,
  MessagingAllowlistFormSchemaType,
} from './types/MessagingAllowlistFormSchemaType';

export function MessagingAllowlist() {
  const {
    data: emailData,
    isLoading: isEmailLoading,
    isError: isEmailError,
  } = useGetMessagingAllowlistQuery({channel: 'email'});
  const {
    data: smsData,
    isLoading: isSmsLoading,
    isError: isSmsError,
  } = useGetMessagingAllowlistQuery({channel: 'sms'});

  const [updateAllowlist] = useUpdateMessagingAllowlistMutation();

  const isLoading = isEmailLoading || isSmsLoading;
  const isError = isEmailError || isSmsError;
  const isReady = isNotNil(emailData) && isNotNil(smsData);

  const defaultValues: MessagingAllowlistFormSchemaType = {
    email: {
      enabled: emailData?.enabled ?? false,
      patterns: (emailData?.patterns ?? []).map((value) => ({value})),
    },
    sms: {
      enabled: smsData?.enabled ?? false,
      patterns: (smsData?.patterns ?? []).map((value) => ({value})),
    },
  };

  const handleSubmit = async (values: MessagingAllowlistFormSchemaType) => {
    const results = await Promise.allSettled(
      CHANNELS.map((channel) =>
        updateAllowlist({
          channel,
          body: {
            enabled: values[channel].enabled,
            patterns: values[channel].patterns
              .map((p) => p.value.trim())
              .filter((p) => p.length > 0),
          },
        }).unwrap()
      )
    );

    const failures = results.filter((r) => r.status === 'rejected');
    if (failures.length === 0) {
      showNotification.success();
      return;
    }
    failures.forEach((failure) => {
      handleApiError(failure.reason);
    });
  };

  return (
    <Main isFullHeight heading={i18n.t('page.demo.messagingAllowlist.title')}>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={60}>
        <Show when={isReady}>
          <Form<MessagingAllowlistFormSchemaType>
            experimentalZodSchema={MessagingAllowlistFormSchema}
            defaultValues={defaultValues}
            onSubmit={handleSubmit}
          >
            {(control, formApi) => (
              <VStack spacing={4}>
                <Grid columns={2}>
                  {CHANNELS.map((channel) => {
                    const isChannelEnabled = formApi.watch(`${channel}.enabled`) ?? false;
                    return (
                      <Card
                        key={channel}
                        title={match(channel)
                          .with('email', () => i18n.t('general.labels.email'))
                          .with('sms', () => i18n.t('general.labels.sms'))
                          .exhaustive()}
                        control={{
                          type: 'switch',
                          value: isChannelEnabled,
                          onChange: (value) =>
                            formApi.setValue(`${channel}.enabled`, value, {
                              shouldDirty: true,
                            }),
                          'data-testid': `messagingAllowlist-${channel}-enabled`,
                        }}
                      >
                        <PatternList
                          control={control}
                          formApi={formApi}
                          channel={channel}
                          isDisabled={!isChannelEnabled}
                        />
                      </Card>
                    );
                  })}
                </Grid>
                <Card variant="inlineGrey">
                  <Text size="small" color="informational">
                    <Trans
                      i18nKey="page.demo.messagingAllowlist.description"
                      components={{code: <Text color="link" size="small" inline />}}
                    />
                  </Text>
                </Card>
                <Align right>
                  <ButtonGroup align="right">
                    <Button
                      variant="secondary"
                      title={i18n.t('general.actions.discard')}
                      onClick={() => formApi.reset(defaultValues)}
                      data-testid="messagingAllowlist-discard"
                    />
                    <FormButton
                      type="submit"
                      variant="primary"
                      control={control}
                      title={i18n.t('general.actions.save')}
                      data-testid="messagingAllowlist-save"
                    />
                  </ButtonGroup>
                </Align>
              </VStack>
            )}
          </Form>
        </Show>
      </DataStatus>
    </Main>
  );
}
